// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { resolveDemoDataEnabled } from "./demoDataFlag";
import { resolveDevelopmentMode, resolveTestMode } from "./runtimeModeFlags";

type RuntimeEnvRecord = Record<string, string | boolean | undefined>;

function readGlobalRuntimeEnv(): RuntimeEnvRecord {
  return (
    (globalThis as { __RIDDLE_ENV__?: RuntimeEnvRecord }).__RIDDLE_ENV__ ?? {}
  );
}

function readViteEnv(): RuntimeEnvRecord {
  return import.meta.env as RuntimeEnvRecord;
}

export function readRuntimeEnvRecords(): RuntimeEnvRecord[] {
  return [readGlobalRuntimeEnv(), readViteEnv()];
}

export function getRuntimeEnvValue(key: string): string | undefined {
  const runtimeValue = readGlobalRuntimeEnv()[key];
  if (typeof runtimeValue === "string" && runtimeValue.length > 0) {
    return runtimeValue;
  }

  const viteValue = readViteEnv()[key];
  return typeof viteValue === "string" && viteValue.length > 0
    ? viteValue
    : undefined;
}

export function isTestMode(): boolean {
  return resolveTestMode(readGlobalRuntimeEnv(), readViteEnv());
}

/**
 * True in a production build (Vite's build-time PROD flag). Runtime config
 * such as siteconfig.json cannot change it (issue 910).
 */
export function isProductionBuild(): boolean {
  return readViteEnv().PROD === true;
}

/**
 * True only when demo data was enabled explicitly (`VITE_DEMO_DATA` or the
 * runtime `DEMO_DATA`). Defaults to false and is always false in production
 * builds (issue 910).
 */
export function isDemoDataEnabled(): boolean {
  return resolveDemoDataEnabled(readGlobalRuntimeEnv(), readViteEnv());
}

export function isDevelopmentMode(): boolean {
  return resolveDevelopmentMode(readGlobalRuntimeEnv(), readViteEnv());
}

export const mainBuildEnv: RuntimeEnvRecord = readViteEnv();
